import { FC, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Label,
  Modal,
  Select,
  Spinner,
  Table,
  TextInput,
} from "flowbite-react";
import {
  HiKey,
  HiPlus,
  HiTrash,
  HiClipboardCopy,
  HiExclamation,
} from "react-icons/hi";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";
import { useGetAllCompaniesQuery } from "@/api/companyApi/company";
import {
  useGetApiClientsQuery,
  useCreateApiClientMutation,
  useUpdateApiClientMutation,
  useDeleteApiClientMutation,
  type ApiClient,
  type CreateApiClientResponse,
} from "@/api/apiClientApi/apiClient";

const ApiClientList: FC = () => {
  const { t } = useTranslation(["apiClient", "common"]);
  const user = useCurrentUser();
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;

  const [showCreate, setShowCreate] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  // Holds the one-time secret returned by the create call.
  const [newCredentials, setNewCredentials] =
    useState<CreateApiClientResponse["client"] | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiClient | null>(null);
  const [editing, setEditing] = useState<ApiClient | null>(null);
  const [editWebhook, setEditWebhook] = useState("");

  const { data: clients, isLoading } = useGetApiClientsQuery(undefined, {
    skip: !isSuperAdmin,
    refetchOnMountOrArgChange: true,
  });
  const { data: companiesData } = useGetAllCompaniesQuery(
    { page: 1, pageSize: 200 },
    { skip: !isSuperAdmin }
  );

  const [createApiClient, { isLoading: isCreating }] = useCreateApiClientMutation();
  const [updateApiClient, { isLoading: isUpdating }] = useUpdateApiClientMutation();
  const [deleteApiClient, { isLoading: isDeleting }] = useDeleteApiClientMutation();

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  const companies = companiesData?.items ?? [];
  const rows = clients ?? [];

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("apiClient:copied"));
    } catch {
      toast.error(t("apiClient:copyFailed"));
    }
  };

  const handleCreate = async () => {
    if (!companyId) {
      toast.error(t("apiClient:selectCompanyFirst"));
      return;
    }
    try {
      const res = await createApiClient({
        company_id: Number(companyId),
        webhook_url: webhookUrl.trim() || undefined,
      }).unwrap();
      setNewCredentials(res.client);
      setShowCreate(false);
      setCompanyId("");
      setWebhookUrl("");
    } catch {
      toast.error(t("apiClient:createFailed"));
    }
  };

  const handleToggleStatus = async (client: ApiClient) => {
    try {
      await updateApiClient({
        id: client.id,
        status: client.status === "active" ? "suspended" : "active",
      }).unwrap();
      toast.success(t("apiClient:updated"));
    } catch {
      toast.error(t("apiClient:updateFailed"));
    }
  };

  const handleSaveWebhook = async () => {
    if (!editing) return;
    try {
      await updateApiClient({
        id: editing.id,
        webhook_url: editWebhook.trim() || null,
      }).unwrap();
      toast.success(t("apiClient:updated"));
      setEditing(null);
    } catch {
      toast.error(t("apiClient:updateFailed"));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteApiClient(deleteTarget.id).unwrap();
      toast.success(t("apiClient:deleted"));
      setDeleteTarget(null);
    } catch {
      toast.error(t("apiClient:deleteFailed"));
    }
  };

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/30">
              <HiKey className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("apiClient:title")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("apiClient:subtitle", { count: rows.length })}
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)}>
            <HiPlus className="mr-2 h-4 w-4" />
            {t("apiClient:create")}
          </Button>
        </div>

        <Card>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="xl" />
            </div>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-gray-500 dark:text-gray-400">
              {t("apiClient:empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>{t("apiClient:table.company")}</Table.HeadCell>
                  <Table.HeadCell>{t("apiClient:table.clientId")}</Table.HeadCell>
                  <Table.HeadCell>{t("apiClient:table.webhook")}</Table.HeadCell>
                  <Table.HeadCell>{t("apiClient:table.status")}</Table.HeadCell>
                  <Table.HeadCell>{t("apiClient:table.actions")}</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {rows.map((client) => (
                    <Table.Row
                      key={client.id}
                      className="bg-white dark:border-gray-700 dark:bg-gray-800"
                    >
                      <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                        {client.company_name ||
                          companies.find((c) => c.company_id === client.company_id)
                            ?.company_name ||
                          `#${client.company_id}`}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <code className="rounded bg-gray-100 px-2 py-1 text-xs dark:bg-gray-700">
                            {client.oauth_client_id}
                          </code>
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => copy(client.oauth_client_id)}
                          >
                            <HiClipboardCopy className="h-3 w-3" />
                          </Button>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="max-w-xs truncate">
                        {client.webhook_url || "—"}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={client.status === "active" ? "success" : "gray"}
                          className="w-fit"
                        >
                          {client.status === "active"
                            ? t("apiClient:status.active")
                            : t("apiClient:status.suspended")}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => {
                              setEditing(client);
                              setEditWebhook(client.webhook_url ?? "");
                            }}
                          >
                            {t("apiClient:editWebhook")}
                          </Button>
                          <Button
                            size="xs"
                            color="light"
                            disabled={isUpdating}
                            onClick={() => handleToggleStatus(client)}
                          >
                            {client.status === "active"
                              ? t("apiClient:suspend")
                              : t("apiClient:activate")}
                          </Button>
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => setDeleteTarget(client)}
                          >
                            <HiTrash className="h-3 w-3" />
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Create credentials */}
      <Modal show={showCreate} onClose={() => setShowCreate(false)}>
        <Modal.Header>{t("apiClient:createTitle")}</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="company">{t("apiClient:form.company")}</Label>
              <Select
                id="company"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">{t("apiClient:form.selectCompany")}</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.company_name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="webhook">{t("apiClient:form.webhook")}</Label>
              <TextInput
                id="webhook"
                placeholder="https://example.com/webhooks/tickets"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("apiClient:form.webhookHelp")}
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? <Spinner size="sm" /> : t("apiClient:form.submit")}
          </Button>
          <Button color="gray" onClick={() => setShowCreate(false)}>
            {t("common:cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* One-time secret reveal */}
      <Modal show={!!newCredentials} onClose={() => setNewCredentials(null)} size="lg">
        <Modal.Header>{t("apiClient:credentialsTitle")}</Modal.Header>
        <Modal.Body>
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
            <HiExclamation className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{t("apiClient:secretWarning")}</span>
          </div>
          <div className="space-y-3">
            <div>
              <Label>{t("apiClient:clientId")}</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-gray-100 px-3 py-2 text-xs dark:bg-gray-700">
                  {newCredentials?.client_id}
                </code>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => copy(newCredentials?.client_id ?? "")}
                >
                  <HiClipboardCopy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>{t("apiClient:clientSecret")}</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-gray-100 px-3 py-2 text-xs dark:bg-gray-700">
                  {newCredentials?.client_secret}
                </code>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => copy(newCredentials?.client_secret ?? "")}
                >
                  <HiClipboardCopy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setNewCredentials(null)}>
            {t("apiClient:understood")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit webhook */}
      <Modal show={!!editing} onClose={() => setEditing(null)}>
        <Modal.Header>{t("apiClient:editWebhookTitle")}</Modal.Header>
        <Modal.Body>
          <Label htmlFor="edit-webhook">{t("apiClient:form.webhook")}</Label>
          <TextInput
            id="edit-webhook"
            placeholder="https://example.com/webhooks/tickets"
            value={editWebhook}
            onChange={(e) => setEditWebhook(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("apiClient:form.webhookHelp")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSaveWebhook} disabled={isUpdating}>
            {isUpdating ? <Spinner size="sm" /> : t("common:save")}
          </Button>
          <Button color="gray" onClick={() => setEditing(null)}>
            {t("common:cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirm */}
      <Modal show={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="md">
        <Modal.Header>{t("apiClient:deleteTitle")}</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t("apiClient:deleteConfirm", {
              client: deleteTarget?.oauth_client_id,
            })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? <Spinner size="sm" /> : t("common:delete")}
          </Button>
          <Button color="gray" onClick={() => setDeleteTarget(null)}>
            {t("common:cancel")}
          </Button>
        </Modal.Footer>
      </Modal>
    </NavbarSidebarLayout>
  );
};

export default ApiClientList;
